import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isAtLeast } from '../utils/roles';
import {
  fetchLoginAuditLogs,
  LoginAuditEntry,
  LoginAuditQueryParams,
} from '../api/loginAudit';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
} from '../components/legacy-ball/table-management/designTokens';

// ── Constants ─────────────────────────────────────────────────

const DEFAULT_PAGE_SIZE = 50;

// ── AI Analytics Dashboard Component ──────────────────────────

function LoginAnalyticsDashboard({ entries }: { entries: LoginAuditEntry[]; total: number }) {
  const [showInsights, setShowInsights] = useState(true);

  if (entries.length === 0) return null;

  // ── Computed Metrics ────────────────────────────────────────
  const successCount = entries.filter((e) => e.outcome === 'success').length;
  const failureCount = entries.filter((e) => e.outcome === 'failure').length;
  const successRate = entries.length > 0 ? Math.round((successCount / entries.length) * 100) : 0;

  // Hourly activity distribution
  const hourlyActivity: number[] = new Array(24).fill(0);
  const hourlyFailures: number[] = new Array(24).fill(0);
  entries.forEach((e) => {
    const hour = new Date(e.timestamp).getHours();
    hourlyActivity[hour]++;
    if (e.outcome === 'failure') hourlyFailures[hour]++;
  });
  const maxHourly = Math.max(...hourlyActivity, 1);

  // Geo distribution
  const geoMap: Record<string, { count: number; failures: number }> = {};
  entries.forEach((e) => {
    const loc = e.city && e.countryCode ? `${e.city}, ${e.countryCode}` : (e.country || e.countryCode || null);
    if (loc) {
      if (!geoMap[loc]) geoMap[loc] = { count: 0, failures: 0 };
      geoMap[loc].count++;
      if (e.outcome === 'failure') geoMap[loc].failures++;
    }
  });
  const topGeos = Object.entries(geoMap).sort((a, b) => b[1].count - a[1].count).slice(0, 8);
  const maxGeo = topGeos.length > 0 ? topGeos[0][1].count : 1;

  // Device breakdown
  const deviceMap: Record<string, number> = {};
  entries.forEach((e) => {
    if (!e.userAgent) return;
    let browser = 'Other';
    if (e.userAgent.includes('Chrome') && !e.userAgent.includes('Edg')) browser = 'Chrome';
    else if (e.userAgent.includes('Edg')) browser = 'Edge';
    else if (e.userAgent.includes('Firefox')) browser = 'Firefox';
    else if (e.userAgent.includes('Safari') && !e.userAgent.includes('Chrome')) browser = 'Safari';
    deviceMap[browser] = (deviceMap[browser] || 0) + 1;
  });
  const mobileCount = entries.filter((e) => /iPhone|iPad|Android|Mobile/i.test(e.userAgent)).length;
  const desktopCount = entries.length - mobileCount;

  // AI Threat Analysis
  const ipFailures: Record<string, number> = {};
  entries.filter((e) => e.outcome === 'failure').forEach((e) => {
    ipFailures[e.ipAddress] = (ipFailures[e.ipAddress] || 0) + 1;
  });
  const suspiciousIPs = Object.entries(ipFailures).filter(([, c]) => c >= 3);
  const uniqueIPs = new Set(entries.map((e) => e.ipAddress)).size;
  const uniqueLocations = new Set(entries.filter((e) => e.city).map((e) => `${e.city},${e.countryCode}`)).size;

  // Threat score (0-100): weighted combination of failure rate, suspicious IPs, unusual patterns
  const threatScore = Math.min(100, Math.round(
    (failureCount / Math.max(entries.length, 1)) * 40 +
    suspiciousIPs.length * 15 +
    (failureCount > 10 ? 20 : failureCount > 5 ? 10 : 0)
  ));

  // AI-generated insights
  const insights: string[] = [];
  if (successRate >= 95) insights.push('🟢 Authentication health is excellent — over 95% success rate.');
  else if (successRate >= 80) insights.push('🟡 Moderate failure rate detected — consider reviewing failed attempts for patterns.');
  else insights.push('🔴 High failure rate — potential credential stuffing or misconfigured accounts.');

  if (suspiciousIPs.length > 0) {
    insights.push(`⚠️ ${suspiciousIPs.length} IP${suspiciousIPs.length > 1 ? 's' : ''} with 3+ failed attempts detected (possible brute force).`);
  }

  const peakHour = hourlyActivity.indexOf(Math.max(...hourlyActivity));
  insights.push(`📊 Peak login activity at ${peakHour}:00 (${hourlyActivity[peakHour]} attempts).`);

  if (mobileCount > desktopCount) {
    insights.push(`📱 Mobile-first audience: ${Math.round((mobileCount / entries.length) * 100)}% of logins from mobile devices.`);
  }

  if (uniqueLocations > 5) {
    insights.push(`🌍 Geographically distributed — logins from ${uniqueLocations} distinct locations.`);
  }

  const threatColor = threatScore < 20 ? '#16a34a' : threatScore < 50 ? '#d97706' : '#dc2626';
  const threatLabel = threatScore < 20 ? 'LOW' : threatScore < 50 ? 'MEDIUM' : 'HIGH';

  const dashStyles: Record<string, React.CSSProperties> = {
    container: { marginBottom: '24px', borderRadius: '16px', background: 'linear-gradient(135deg, #0f2d1f 0%, #1a4a2e 50%, #0d2418 100%)', padding: '24px', color: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
    title: { fontSize: '1.1rem', fontWeight: 700, letterSpacing: '0.5px' },
    toggleBtn: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', padding: '6px 14px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' },
    card: { background: 'rgba(255,255,255,0.08)', borderRadius: '12px', padding: '16px', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.1)' },
    cardTitle: { fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '1px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' },
    barRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
    barLabel: { fontSize: '0.72rem', color: 'rgba(255,255,255,0.7)', minWidth: '80px', textAlign: 'right' as const },
    barTrack: { flex: 1, height: '8px', borderRadius: '4px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' },
    insightRow: { padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.08)', fontSize: '0.82rem', lineHeight: 1.5 },
    ringContainer: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px' },
    bigStat: { fontSize: '2.2rem', fontWeight: 800, lineHeight: 1 },
    statLabel: { fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' as const, marginTop: '4px' },
  };

  return (
    <div style={dashStyles.container}>
      <div style={dashStyles.header}>
        <div>
          <div style={dashStyles.title}>🤖 AI Login Intelligence Board</div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>Real-time authentication analytics • {entries.length} events analyzed</div>
        </div>
        <button style={dashStyles.toggleBtn} onClick={() => setShowInsights(!showInsights)}>
          {showInsights ? 'Hide Analytics' : 'Show Analytics'}
        </button>
      </div>

      {showInsights && (
        <>
          {/* Row 1: Key Metrics */}
          <div style={{ ...dashStyles.grid, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: '16px' }}>
            <div style={dashStyles.card}>
              <div style={dashStyles.ringContainer}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...dashStyles.bigStat, color: '#4ade80' }}>{successRate}%</div>
                  <div style={dashStyles.statLabel}>Success Rate</div>
                </div>
              </div>
            </div>
            <div style={dashStyles.card}>
              <div style={dashStyles.ringContainer}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...dashStyles.bigStat, color: threatColor }}>{threatScore}</div>
                  <div style={dashStyles.statLabel}>Threat Score ({threatLabel})</div>
                </div>
              </div>
            </div>
            <div style={dashStyles.card}>
              <div style={dashStyles.ringContainer}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...dashStyles.bigStat, color: '#60a5fa' }}>{uniqueIPs}</div>
                  <div style={dashStyles.statLabel}>Unique IPs</div>
                </div>
              </div>
            </div>
            <div style={dashStyles.card}>
              <div style={dashStyles.ringContainer}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ ...dashStyles.bigStat, color: '#c084fc' }}>{uniqueLocations}</div>
                  <div style={dashStyles.statLabel}>Locations</div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Charts */}
          <div style={dashStyles.grid}>
            {/* Hourly Activity Heatmap */}
            <div style={dashStyles.card}>
              <div style={dashStyles.cardTitle}>⏰ 24-Hour Activity Timeline</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '80px' }}>
                {hourlyActivity.map((count, hour) => (
                  <div key={hour} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                    <div
                      style={{
                        width: '100%',
                        height: `${Math.max(4, (count / maxHourly) * 70)}px`,
                        borderRadius: '2px 2px 0 0',
                        background: hourlyFailures[hour] > 0
                          ? `linear-gradient(to top, #ef4444 ${(hourlyFailures[hour] / Math.max(count, 1)) * 100}%, #4ade80 0%)`
                          : count > 0 ? '#4ade80' : 'rgba(255,255,255,0.1)',
                        transition: 'height 0.3s ease',
                      }}
                      title={`${hour}:00 — ${count} logins (${hourlyFailures[hour]} failures)`}
                    />
                    {hour % 6 === 0 && (
                      <span style={{ fontSize: '0.55rem', color: 'rgba(255,255,255,0.4)' }}>{hour}</span>
                    )}
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '12px', marginTop: '8px', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>
                <span>🟢 Success</span><span>🔴 Failure</span>
              </div>
            </div>

            {/* Geo Distribution */}
            <div style={dashStyles.card}>
              <div style={dashStyles.cardTitle}>🌍 Geographic Distribution</div>
              {topGeos.length === 0 ? (
                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', padding: '20px 0', textAlign: 'center' }}>
                  No geo data yet — new logins will populate this
                </div>
              ) : (
                <div>
                  {topGeos.map(([loc, data]) => (
                    <div key={loc} style={dashStyles.barRow}>
                      <span style={dashStyles.barLabel}>{loc}</span>
                      <div style={dashStyles.barTrack}>
                        <div style={{
                          height: '100%',
                          width: `${(data.count / maxGeo) * 100}%`,
                          borderRadius: '4px',
                          background: data.failures > 0 ? 'linear-gradient(to right, #4ade80, #fbbf24)' : '#4ade80',
                          transition: 'width 0.5s ease',
                        }} />
                      </div>
                      <span style={{ fontSize: '0.72rem', fontWeight: 600, minWidth: '24px' }}>{data.count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Device Fingerprints */}
            <div style={dashStyles.card}>
              <div style={dashStyles.cardTitle}>🖥️ Device & Browser Fingerprints</div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                {Object.entries(deviceMap).sort((a, b) => b[1] - a[1]).map(([browser, count]) => (
                  <span key={browser} style={{
                    padding: '4px 10px',
                    borderRadius: '12px',
                    background: 'rgba(255,255,255,0.12)',
                    fontSize: '0.72rem',
                    fontWeight: 500,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}>
                    {browser}: {count}
                  </span>
                ))}
              </div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>📱 {mobileCount}</div>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>Mobile</div>
                </div>
                <div style={{ flex: 1, height: '12px', borderRadius: '6px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%',
                    width: `${entries.length > 0 ? (mobileCount / entries.length) * 100 : 0}%`,
                    background: 'linear-gradient(to right, #60a5fa, #c084fc)',
                    borderRadius: '6px',
                  }} />
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>🖥️ {desktopCount}</div>
                  <div style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>Desktop</div>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div style={dashStyles.card}>
              <div style={dashStyles.cardTitle}>🧠 AI Security Insights</div>
              {insights.map((insight, i) => (
                <div key={i} style={dashStyles.insightRow}>{insight}</div>
              ))}
              {suspiciousIPs.length > 0 && (
                <div style={{ marginTop: '10px', padding: '10px', borderRadius: '8px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#fca5a5', marginBottom: '4px' }}>
                    🚨 Suspicious IPs ({suspiciousIPs.length})
                  </div>
                  {suspiciousIPs.slice(0, 3).map(([ip, count]) => (
                    <div key={ip} style={{ fontSize: '0.72rem', fontFamily: 'monospace', color: 'rgba(255,255,255,0.7)' }}>
                      {ip} — {count} failed attempts
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: PRIMARY, fontSize: '1.75rem', margin: '0 0 0.5rem 0' },
  subtitle: { color: TEXT_MUTED, fontSize: '0.95rem', marginBottom: '1.5rem' },
  accessDenied: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: '#d32f2f',
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '12px',
    marginBottom: '16px',
    alignItems: 'flex-end',
  },
  filterGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px',
  },
  filterLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.3px',
  },
  select: {
    padding: '8px 12px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '13px',
    backgroundColor: BG_CARD,
    minWidth: '140px',
  },
  input: {
    padding: '8px 12px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
    minWidth: '180px',
  },
  dateInput: {
    padding: '8px 12px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '13px',
    boxSizing: 'border-box' as const,
    minWidth: '140px',
  },
  clearBtn: {
    padding: '8px 14px',
    backgroundColor: '#f5f5f5',
    color: TEXT_DARK,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    backgroundColor: BG_CARD,
    borderRadius: '8px',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  th: {
    textAlign: 'left' as const,
    padding: '12px 16px',
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    color: TEXT_MUTED,
    borderBottom: `2px solid ${PRIMARY}`,
    fontWeight: 600,
  },
  td: {
    padding: '12px 16px',
    fontSize: '14px',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    color: TEXT_DARK,
    verticalAlign: 'middle' as const,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    padding: '12px 0',
  },
  paginationInfo: {
    fontSize: '13px',
    color: TEXT_MUTED,
  },
  paginationControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  pageBtn: {
    padding: '6px 12px',
    backgroundColor: BG_CARD,
    color: PRIMARY,
    border: `1px solid ${PRIMARY}`,
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  pageBtnDisabled: {
    padding: '6px 12px',
    backgroundColor: '#f5f5f5',
    color: '#999',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  pageSizeSelect: {
    padding: '6px 10px',
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '4px',
    fontSize: '12px',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '14px',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '3rem',
    color: TEXT_MUTED,
    fontSize: '14px',
  },
  error: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '10px 16px',
    borderRadius: '8px',
    marginBottom: '16px',
    fontSize: '13px',
    border: '1px solid #f5c6cb',
  },
  dateRangeError: {
    fontSize: '11px',
    color: '#d32f2f',
    marginTop: '2px',
  },
  outcomeBadge: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '11px',
    fontWeight: 600,
  },
  userAgentCell: {
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
};

// ── Helpers ───────────────────────────────────────────────────

function getOutcomeBadgeStyle(outcome: string): React.CSSProperties {
  if (outcome === 'success') {
    return { backgroundColor: '#e8f5e9', color: '#1a6b3c' };
  }
  return { backgroundColor: '#fdecea', color: '#c62828' };
}

function formatTimestamp(ts: string): string {
  const date = new Date(ts);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatFailureReason(reason: string | null): string {
  if (!reason) return '—';
  return reason.replace(/_/g, ' ');
}

// ── Component ─────────────────────────────────────────────────

export function LoginAuditPage() {
  const { user } = useAuth();

  // Filter state
  const [identifier, setIdentifier] = useState('');
  const [outcome, setOutcome] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  // Data state
  const [entries, setEntries] = useState<LoginAuditEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Date range validation
  const dateRangeValid = (() => {
    if (!startDate || !endDate) return true;
    return new Date(startDate) < new Date(endDate);
  })();

  const totalPages = Math.ceil(total / pageSize) || 1;

  const loadData = useCallback(async () => {
    if (!dateRangeValid) return;

    setLoading(true);
    setError('');

    const params: LoginAuditQueryParams = {
      page,
      pageSize,
    };

    if (identifier.trim()) params.identifier = identifier.trim();
    if (outcome) params.outcome = outcome as 'success' | 'failure';
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    try {
      const response = await fetchLoginAuditLogs(params);
      setEntries(response.data);
      setTotal(response.total);
    } catch {
      setError('Failed to load login audit logs. Please try again.');
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [identifier, outcome, startDate, endDate, page, pageSize, dateRangeValid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [identifier, outcome, startDate, endDate, pageSize]);

  const handleClearFilters = () => {
    setIdentifier('');
    setOutcome('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  // Access check
  if (!user || !isAtLeast(user.role, 'Admin')) {
    return (
      <div style={styles.accessDenied}>
        <h2>Access Denied</h2>
        <p>You do not have permission to view login audit logs.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Login Audit Trail</h1>
      <p style={styles.subtitle}>
        Monitor authentication attempts — successful logins and failed attempts with failure reasons.
      </p>

      {/* AI Analytics Dashboard */}
      <LoginAnalyticsDashboard entries={entries} total={total} />

      {/* Filters */}
      <div style={styles.filtersRow}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Identifier</label>
          <input
            style={styles.input}
            type="text"
            placeholder="Search email or ticket number..."
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            aria-label="Filter by identifier"
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Outcome</label>
          <select
            style={styles.select}
            value={outcome}
            onChange={(e) => setOutcome(e.target.value)}
            aria-label="Filter by outcome"
          >
            <option value="">All</option>
            <option value="success">Success</option>
            <option value="failure">Failure</option>
          </select>
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Start Date</label>
          <input
            style={styles.dateInput}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            aria-label="Filter start date"
          />
        </div>

        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>End Date</label>
          <input
            style={styles.dateInput}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            aria-label="Filter end date"
          />
        </div>

        <button
          style={styles.clearBtn}
          onClick={handleClearFilters}
          aria-label="Clear all filters"
        >
          Clear Filters
        </button>
      </div>

      {/* Date range validation error */}
      {!dateRangeValid && (
        <p style={styles.dateRangeError}>
          Start date must be before end date.
        </p>
      )}

      {/* Error */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Loading */}
      {loading && <div style={styles.loading}>Loading login audit logs...</div>}

      {/* Table */}
      {!loading && entries.length > 0 && (
        <>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Timestamp</th>
                <th style={styles.th}>Identifier</th>
                <th style={styles.th}>Outcome</th>
                <th style={styles.th}>Failure Reason</th>
                <th style={styles.th}>Location</th>
                <th style={styles.th}>IP Address</th>
                <th style={styles.th}>User Agent</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td style={styles.td}>{formatTimestamp(entry.timestamp)}</td>
                  <td style={styles.td}>{entry.identifier}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.outcomeBadge, ...getOutcomeBadgeStyle(entry.outcome) }}>
                      {entry.outcome}
                    </span>
                  </td>
                  <td style={styles.td}>{formatFailureReason(entry.failureReason)}</td>
                  <td style={styles.td}>
                    {entry.city || entry.country
                      ? `${entry.city || ''}${entry.city && entry.countryCode ? ', ' : ''}${entry.countryCode || entry.country || ''}`
                      : '—'}
                  </td>
                  <td style={styles.td}>{entry.ipAddress}</td>
                  <td style={{ ...styles.td, ...styles.userAgentCell }} title={entry.userAgent}>
                    {entry.userAgent}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div style={styles.pagination}>
            <span style={styles.paginationInfo}>
              Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total} entries
            </span>

            <div style={styles.paginationControls}>
              <label style={{ fontSize: '12px', color: TEXT_MUTED }}>
                Per page:
                <select
                  style={{ ...styles.pageSizeSelect, marginLeft: '4px' }}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label="Page size"
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
              </label>

              <button
                style={page <= 1 ? styles.pageBtnDisabled : styles.pageBtn}
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                ← Prev
              </button>

              <span style={{ fontSize: '12px', color: TEXT_MUTED }}>
                Page {page} of {totalPages}
              </span>

              <button
                style={page >= totalPages ? styles.pageBtnDisabled : styles.pageBtn}
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!loading && !error && entries.length === 0 && (
        <div style={styles.emptyState}>
          <p>No login audit entries found.</p>
          <p style={{ fontSize: '12px', color: '#999' }}>
            Try adjusting your filters or date range.
          </p>
        </div>
      )}
    </div>
  );
}
